/* eslint-disable no-param-reassign */
import { navigateTo, onNuxtReady } from '#imports'
import { type Reactive, type Ref, nextTick, watch } from 'vue'
import {
  type LocationQueryRaw,
  type RouteLocationNormalizedLoaded,
  useRoute,
} from 'vue-router'

import { parse } from '@backmarket/utils/query-string/parse'
import { stringify } from '@backmarket/utils/query-string/stringify'

/**
 * Keep the outlet PLP's filter state in sync with the URL so that navigating
 * to a PDP and coming back (browser back) restores the exact filters, price
 * range and page the user had selected.
 *
 * Filters + price live in the route hash (`#facetName=v1,v2&price=min-max`),
 * the page number lives in `?p=N`. The hash is used (rather than the query)
 * so filters don't get sent to the server or interfere with other query
 * params. On mount we rehydrate from the URL once, then watchers push every
 * subsequent change back to the URL.
 *
 * Mirrors the main marketplace PLP's `useFiltersFromUrl` but without its
 * "open facets" concept, which the outlet doesn't use.
 *
 * @param filtersInput  - reactive dict of selected facet values
 * @param priceFilter   - ref holding the selected [min, max] price range
 * @param pageInput     - ref holding the current page number
 * @param categorySlug  - ref holding the current category slug; when it
 *                        changes we rehydrate from the new URL so deep-links
 *                        with a hash work across category navigation.
 */
export function useOutletFiltersFromUrl(
  filtersInput: Reactive<Record<string, string[]>>,
  priceFilter: Ref<[number, number] | undefined>,
  pageInput: Ref<number>,
  categorySlug: Ref<string>,
  {
    route = useRoute(),
  }: {
    route?: RouteLocationNormalizedLoaded
  } = {},
) {
  function getFiltersFromUrl() {
    const { price, ...parsedQuery } = route.hash
      ? parse(route.hash)
      : { price: [] }

    const p = route.query
      ? parseInt(route.query.p?.toString() || '', 10) || 1
      : 1

    if (Array.isArray(price) && price.length === 2 && price[0] && price[1]) {
      priceFilter.value = [parseInt(price[0], 10), parseInt(price[1], 10)]
    }

    for (const [key, val] of Object.entries(parsedQuery)) {
      if (Array.isArray(val)) {
        filtersInput[key] = val.filter((item) => item !== null) as string[]
      } else if (val !== null) {
        filtersInput[key] = [val]
      }
    }

    if (p > 1) {
      pageInput.value = p
    }
  }

  function cleanQueryParams(queryParams: LocationQueryRaw) {
    if (queryParams.p === 1) {
      const { p: _p, ...rest } = queryParams

      return rest
    }

    return queryParams
  }

  async function pushHashFiltersToRouter() {
    // Reset to the first page when refinements change, otherwise the user
    // can end up on page 5 of a filtered set that only has 1 page.
    pageInput.value = 1

    const filters = priceFilter.value
      ? { ...filtersInput, price: priceFilter.value }
      : filtersInput
    const hashForActiveFilters = stringify(filters)

    if (route.name) {
      await navigateTo({
        name: route.name,
        query: cleanQueryParams({ ...route.query, p: pageInput.value }),
        params: route.params,
        hash: hashForActiveFilters ? `#${hashForActiveFilters}` : '',
      })
    }
  }

  async function pushQueryFiltersToRouter() {
    if (route.name) {
      await navigateTo({
        name: route.name,
        query: cleanQueryParams({ ...route.query, p: pageInput.value }),
        params: route.params,
        hash: route.hash,
      })
    }
  }

  let initialSyncCompleted = false
  onNuxtReady(async () => {
    getFiltersFromUrl()
    // Ensures the watcher fires and is blocked before we open the gate,
    // preventing a page reset when loading with ?p=2 and hash filters.
    await nextTick()
    initialSyncCompleted = true
  })

  watch(
    [filtersInput, priceFilter],
    async () => {
      if (!initialSyncCompleted) return
      await pushHashFiltersToRouter()
    },
    { deep: true },
  )
  watch(pageInput, async () => {
    if (!initialSyncCompleted) return
    await pushQueryFiltersToRouter()
  })

  // Rehydrate when the user navigates between outlet categories without a
  // full remount (same component, different :slug param). Runs after the
  // page-level category watcher has reset the refs, so URL filters — if any
  // — take precedence over the empty defaults.
  watch(categorySlug, async () => {
    if (!initialSyncCompleted) return
    await nextTick()
    getFiltersFromUrl()
  })
}
